import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import WarningIcon from "@mui/icons-material/Warning";
import { red } from "@mui/material/colors";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// Data
import MDButton from "components/MDButton";

import { validateRequired, validateText } from "../validations";
import Swal from "sweetalert2";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";

function Notification() {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editedRow, setEditedRow] = useState(null); // This state seems unused, consider removing
  const [Input, setInput] = useState({});
  const [editInput, setEditInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteNotificationId, setDeleteNotificationId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const email = localStorage.getItem("email");
  const navigate = useNavigate();

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const columns = [
    { Header: "Sr.No.", accessor: "serialNumber", sortable: true },
    { Header: "Title", accessor: "title", sortable: true },
    { Header: "Message", accessor: "message", sortable: true },
    { Header: "Schedule Date", accessor: "schedule_date", sortable: true },
    { Header: "Schedule Time", accessor: "schedule_time", sortable: true },
    { Header: "Options", accessor: "option", sortable: false },
  ];

  const handleClose = () => {
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    setOpenAddModal(false);
    // Clear form inputs and validation errors on close
    setInput({});
    setEditInput({});
    setValidationErrors({});
    setIsSaveEnabled(false);
    setIsEditEnabled(false);
    // Reset pagination to first page
    setPage(0);
  };

  useEffect(() => {
    disableBackNavigation();
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get("/push_notification/fetch", {
        params: { email },
      });
      setNotifications(res.data || []);
      // Reset page to 0 if the current page becomes out of bounds after fetch
      if (page * rowsPerPage >= (res.data || []).length && (res.data || []).length > 0) {
        setPage(0);
      } else if ((res.data || []).length === 0) {
        setPage(0);
      }
    } catch (error) {
      console.error("Error fetching Notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Centralized validation logic for both add and edit forms
  const validateForm = (currentInput) => {
    let newErrors = {};
    let allFieldsValid = true;

    if (!currentInput.title) {
      newErrors.title = "Title is required.";
      allFieldsValid = false;
    } else {
      newErrors.title = validateText(currentInput.title);
      if (newErrors.title) allFieldsValid = false;
    }

    if (!currentInput.message) {
      newErrors.message = "Message is required.";
      allFieldsValid = false;
    } else {
      newErrors.message = validateText(currentInput.message);
      if (newErrors.message) allFieldsValid = false;
    }

    if (!currentInput.schedule_date) {
      newErrors.schedule_date = "Schedule Date is required.";
      allFieldsValid = false;
    } else {
        newErrors.schedule_date = validateRequired(currentInput.schedule_date);
        if (newErrors.schedule_date) allFieldsValid = false;
    }

    if (!currentInput.schedule_time) {
      newErrors.schedule_time = "Schedule Time is required.";
      allFieldsValid = false;
    } else {
        newErrors.schedule_time = validateRequired(currentInput.schedule_time);
        if (newErrors.schedule_time) allFieldsValid = false;
    }

    setValidationErrors(newErrors);

    const hasNoValidationErrors = Object.values(newErrors).every((error) => !error);
    const finalValidity = allFieldsValid && hasNoValidationErrors;

    // This function doesn't directly set isSaveEnabled or isEditEnabled.
    // Instead, it returns the validity, and the handlers will use it.
    return finalValidity;
  };


  const handleAddChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "schedule_date" || name === "schedule_time") {
      updatedValue = value;
      if (name === "schedule_time" && value) {
        updatedValue = `${value}:00`; // Ensure seconds are present for consistency
      }
    }

    const nextInputState = { ...Input, [name]: updatedValue };
    setInput(nextInputState);

    // Call validation and update save button enablement
    const isValid = validateForm(nextInputState);
    setIsSaveEnabled(isValid);
  };

  const addNotification = async (event) => {
    event.preventDefault();

    const isValid = validateForm(Input); // Final validation check
    if (!isValid) {
        console.warn("Form has validation errors, preventing submission.");
        return;
    }

    try {
      const response = await api.post("/push_notification/add", {
        title: Input.title,
        message: Input.message,
        schedule_date: Input.schedule_date,
        schedule_time: Input.schedule_time,
        user_id: email,
      });

      if (response.data.success) {
        Swal.fire({
          title: "Successfully Inserted!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
        handleClose();
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message || "Server Problem. Please Try Again...",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error adding record:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "An unexpected error occurred",
        icon: "error",
      });
    }
    fetchNotifications();
  };

  const deleteNotification = async (event) => {
    event.preventDefault();
    if (deleteNotificationId) {
      try {
        const response = await api.delete(
          `/push_notification/delete/${deleteNotificationId}`
        );
        setOpenDeleteModal(false);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            text: "Successfully deleted...",
            type: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          fetchNotifications();
        } else {
          Swal.fire({
            icon: "error",
            text:
              response.data.message || "Server Problem. Please Try Again...",
            type: "error",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error("Error deleting Notification:", error);
        Swal.fire({
          icon: "error",
          text: "An unexpected error occurred. Please try again.",
          type: "error",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } else {
      console.error("No Notification ID provided for deletion.");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "schedule_date" || name === "schedule_time") {
      updatedValue = value;
      if (name === "schedule_time" && value) {
        updatedValue = `${value}:00`; // Ensure seconds are present for consistency
      }
    }

    const nextEditInputState = { ...editInput, [name]: updatedValue };
    setEditInput(nextEditInputState);

    // Call validation and update edit button enablement
    const isValid = validateForm(nextEditInputState);
    setIsEditEnabled(isValid);
  };

  const updateNotification = async (event) => {
    event.preventDefault();

    const isValid = validateForm(editInput); // Final validation check
    if (!isValid) {
        console.warn("Form has validation errors, preventing submission.");
        return;
    }

    if (!isEditEnabled) {
      console.warn("Edit is not enabled due to validation errors or missing fields.");
      return;
    }

    try {
      const response = await api.put(`/push_notification/edit/${editInput.id}`, {
        title: editInput.title,
        message: editInput.message,
        schedule_date: editInput.schedule_date,
        schedule_time: editInput.schedule_time,
      });

      if (response.data.success === true) {
        Swal.fire({
          title: "Successfully Updated!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
        handleClose();
        fetchNotifications();
      } else {
        Swal.fire({
          icon: "error",
          text: response.data.msg,
          type: "error",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error updating notification:", error);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred. Please try again.",
        icon: "error",
      });
    }
  };

  // Pagination Handlers
  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate paginated data
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedNotifications = notifications.slice(startIndex, endIndex);


  const data = paginatedNotifications.map((notification, index) => {
    // Format date for display as "DD Month YYYY" (e.g., "04 July 2025")
    const displayDate = notification.schedule_date
      ? new Date(notification.schedule_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
      : '';

    // Format time for display as HH:MM AM/PM (e.g., "11:22 AM")
    const displayTime = notification.schedule_time
      ? new Date(`2000-01-01T${notification.schedule_time}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
      : '';

    // Function to format the date string from API into YYYY-MM-DD for TextField type="date"
    const formatApiDateToInput = (apiDateString) => {
      if (!apiDateString) return '';
      try {
        const date = new Date(apiDateString);
        if (isNaN(date.getTime())) {
          return '';
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error("Error formatting date for input:", apiDateString, error);
        return '';
      }
    };

    return {
      serialNumber: startIndex + index + 1, // Calculate serial number
      title: notification.title,
      message: notification.message,
      schedule_date: displayDate,
      schedule_time: displayTime,
      option: (
        <div style={{ display: "flex" }}>
          <IconButton
            color="success"
            size="medium"
            onClick={() => {
              const notificationData = {
                id: notification.notification_id,
                title: notification.title,
                message: notification.message,
                schedule_date: formatApiDateToInput(notification.schedule_date),
                schedule_time: notification.schedule_time,
              };
              setEditInput(notificationData); // Set all data at once

              // Manually trigger initial validation on the newly set editInput
              // This ensures the button state is correct immediately
              validateForm(notificationData);
              setIsEditEnabled(validateForm(notificationData)); // Set initial state of isEditEnabled

              setOpenEditModal(true);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            size="medium"
            style={{ marginLeft: "5px" }}
            onClick={() => {
              setDeleteNotificationId(notification.notification_id);
              setOpenDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    };
  });

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton
          onLogout={() => {
            navigate("/");
          }}
        />
      </MDBox>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Notifications Table
                </MDTypography>
              </MDBox>
              <MDBox sx={{ textAlign: "right", mt: 2, mr: 4 }}>
                <MDButton color="success" onClick={() => setOpenAddModal(true)}>
                  Add Notification
                </MDButton>

                {/* Add Modal */}
                <Dialog
                  open={openAddModal}
                  onClose={handleClose}
                  fullWidth
                  maxWidth="md"
                >
                  <DialogTitle>Add Notification</DialogTitle>
                  <MDBox component="form">
                    <DialogContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <MDBox>
                            <TextField
                              label="Title"
                              name="title"
                              value={Input.title || ""}
                              onChange={handleAddChange}
                              fullWidth
                              margin="normal"
                              error={!!validationErrors.title}
                              helperText={validationErrors.title}
                            />
                          </MDBox>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <MDBox>
                            <TextField
                              label="Message"
                              name="message"
                              value={Input.message || ""}
                              onChange={handleAddChange}
                              fullWidth
                              margin="normal"
                              error={!!validationErrors.message}
                              helperText={validationErrors.message}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MDBox>
                            <TextField
                              label="Schedule Date"
                              type="date"
                              name="schedule_date"
                              value={Input.schedule_date || ""}
                              onChange={(e) =>
                                handleAddChange({
                                  target: {
                                    name: "schedule_date",
                                    value: e.target.value,
                                  },
                                })
                              }
                              fullWidth
                              margin="normal"
                              error={!!validationErrors.schedule_date}
                              helperText={validationErrors.schedule_date}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                // Set minimum date to today (or later) for add mode
                                min: new Date().toISOString().split('T')[0]
                              }}
                            />
                          </MDBox>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <MDBox>
                            <TextField
                              label="Schedule Time"
                              type="time"
                              value={Input.schedule_time || ""}
                              onChange={(e) => {
                                handleAddChange({
                                  target: {
                                    name: "schedule_time",
                                    value: e.target.value,
                                  },
                                });
                              }}
                              fullWidth
                              margin="normal"
                              error={!!validationErrors.schedule_time}
                              helperText={validationErrors.schedule_time}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          </MDBox>
                        </Grid>
                      </Grid>
                    </DialogContent>
                  </MDBox>

                  <DialogActions>
                    <Button onClick={handleClose} color="primary">
                      Cancel
                    </Button>
                    <MDButton
                      type="button"
                      color="success"
                      onClick={addNotification}
                      disabled={!isSaveEnabled}
                    >
                      Save
                    </MDButton>
                  </DialogActions>
                </Dialog>
              </MDBox>

              {/* Edit Modal */}
              <Dialog
                open={openEditModal}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
              >
                <DialogTitle>Edit Notification</DialogTitle>
                <MDBox component="form">
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Title"
                            name="title"
                            value={editInput.title || ""}
                            onChange={handleEditChange}
                            fullWidth
                            margin="normal"
                            error={!!validationErrors.title}
                            helperText={validationErrors.title}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Message"
                            name="message"
                            value={editInput.message || ""}
                            onChange={handleEditChange}
                            fullWidth
                            margin="normal"
                            error={!!validationErrors.message}
                            helperText={validationErrors.message}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Schedule Date"
                            type="date"
                            value={editInput.schedule_date || ""}
                            onChange={(e) => {
                              handleEditChange({
                                target: {
                                  name: "schedule_date",
                                  value: e.target.value,
                                },
                              });
                            }}
                            fullWidth
                            margin="normal"
                            error={!!validationErrors.schedule_date}
                            helperText={validationErrors.schedule_date}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                                min: new Date().toISOString().split('T')[0]
                            }}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Schedule Time"
                            type="time"
                            value={editInput.schedule_time || ""}
                            onChange={(e) => {
                              handleEditChange({
                                target: {
                                  name: "schedule_time",
                                  value: e.target.value,
                                },
                              });
                            }}
                            fullWidth
                            margin="normal"
                            error={!!validationErrors.schedule_time}
                            helperText={validationErrors.schedule_time}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </MDBox>

                <DialogActions>
                  <Button onClick={handleClose} color="primary">
                    Cancel
                  </Button>
                  <MDButton
                    color="success"
                    type="button"
                    onClick={updateNotification}
                    disabled={!isEditEnabled}
                  >
                    Save
                  </MDButton>
                </DialogActions>
              </Dialog>

              {/* Delete Modal */}
              <Dialog open={openDeleteModal} onClose={handleClose}>
                <MDBox component="form" onSubmit={deleteNotification}>
                  <DialogTitle>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <WarningIcon
                          style={{ color: red[700], fontSize: 40 }}
                        />
                      </Grid>
                      <Grid item>
                        <MDTypography variant="h6" component="span">
                          Confirm Delete
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </DialogTitle>

                  <DialogContent>
                    <MDBox
                      p={2}
                      style={{
                        backgroundColor: red[50],
                        borderLeft: `5px solid ${red[700]}`,
                      }}
                    >
                      <MDTypography variant="body1">
                        Are you sure you want to delete this item?
                      </MDTypography>
                    </MDBox>
                  </DialogContent>
                  <DialogActions>
                    <MDButton onClick={handleClose} style={{ color: "gray" }}>
                      Cancel
                    </MDButton>
                    <MDButton type="submit" color="primary">
                      Delete
                    </MDButton>
                  </DialogActions>
                </MDBox>
              </Dialog>

              <MDBox p={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    {/* Placeholder for loading spinner */}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: columns, rows: data }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    canSearch={true}
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: notifications.length,
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Notification;